import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const strategies = [
  { name: "TC-03: Multi-Timeframe Trend Rotation", img: "/strategy-1.png", cagr: "81.9%", sharpe: "3.50", maxDrawdown: "7.7%", backtestId: "a1b2c3d4-0003-4000-8000-000000000003" },
  { name: "TC-02: Adaptive VIX Scale-In", img: "/strategy-2.png", cagr: "76.9%", sharpe: "4.05", maxDrawdown: "7.1%", backtestId: "a1b2c3d4-0002-4000-8000-000000000002" },
  { name: "TC-09: Policy Hedge Diversifier", img: "/strategy-3.png", cagr: "50.5%", sharpe: "2.68", maxDrawdown: "9.8%", backtestId: "a1b2c3d4-0009-4000-8000-000000000009" },
  { name: "TC-05: Precision Alpha", img: "/strategy-4.png", cagr: "60.1%", sharpe: "3.61", maxDrawdown: "5.3%", backtestId: "a1b2c3d4-0005-4000-8000-000000000005" },
];

export function ProductIntroPage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const prevTheme = useRef<string | null>(null);

  useEffect(() => {
    prevTheme.current = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "arctic");
    return () => {
      if (prevTheme.current) {
        document.documentElement.setAttribute("data-theme", prevTheme.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="landing-page min-h-screen bg-surface-950 text-surface-100 overflow-x-hidden">
      {/* Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-surface-950/95 backdrop-blur-xl border-b border-surface-800/50 shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="TradeCraft" className="w-10 h-10" />
              <div className="hidden sm:block">
                <span className="text-xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                  TradeCraft
                </span>
                <p className="text-xs text-surface-400">Enterprise · by Colvix</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#value" className="text-surface-300 hover:text-primary-400 transition-colors">Value Proposition</a>
              <a href="#use-cases" className="text-surface-300 hover:text-primary-400 transition-colors">Use Cases</a>
              <a href="#architecture" className="text-surface-300 hover:text-primary-400 transition-colors">Architecture</a>
              <a href="#exchanges" className="text-surface-300 hover:text-primary-400 transition-colors">Exchanges</a>
              <a href="#contact" className="text-surface-300 hover:text-primary-400 transition-colors">Contact</a>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="mailto:info@colvix.net"
                className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                Schedule a Demo
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-surface-300 hover:text-surface-100 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-surface-950/95 backdrop-blur-xl border-t border-surface-800/50">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
              {["value", "use-cases", "architecture", "exchanges", "contact"].map((id) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-surface-300 hover:text-primary-400 hover:bg-surface-800/50 transition-colors capitalize"
                >
                  {id.replace(/-/g, " ")}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative min-h-[75vh] flex items-center justify-center pt-20 pb-16 sm:pb-24 overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="/intro.mp4?v=2" type="video/mp4" />
        </video>
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/75 z-[1]" />

        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
          <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-primary-500/5 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:60px_60px] z-[3]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-800/50 border border-surface-700/50 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-surface-300">Built by ex-Wall Street Engineers</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-in" style={{ animationDelay: "100ms" }}>
            <span className="text-surface-100">Algorithmic Trading</span>
            <br />
            <span className="bg-gradient-to-r from-primary-400 via-accent-400 to-primary-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              As a Product Line
            </span>
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto mb-4 animate-fade-in" style={{ animationDelay: "200ms" }}>
            <span style={{ color: "#4ade80", display: "inline", fontWeight: 700 }}>Give your clients access to institutional-grade algorithmic trading strategies — integrated directly into your existing platform.</span>
          </p>
          <p className="text-base text-surface-500 max-w-2xl mx-auto mb-10 animate-fade-in" style={{ animationDelay: "250ms" }}>
            For Banks · Fund Managers · Hedge Funds
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: "300ms" }}>
            <a
              href="mailto:info@colvix.net"
              className="group px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-1 flex items-center gap-3"
            >
              Talk to Our Team
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a
              href="#architecture"
              className="px-8 py-4 text-lg font-semibold rounded-xl bg-surface-800/50 border border-surface-700 text-surface-100 hover:bg-surface-700/50 hover:border-surface-600 transition-all duration-300"
            >
              View Architecture
            </a>
          </div>
        </div>
      </section>

      {/* Platform Demo Video */}
      <section className="py-16 sm:py-24 bg-surface-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-surface-100 mb-3">
              See It In Action
            </h2>
            <p className="text-surface-400">A quick look at how TradeCraft works.</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-surface-700/50 shadow-2xl">
            <video
              controls
              playsInline
              className="w-full"
            >
              <source src="/TradeCraft.mp4?v=2" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Strategy Showcase */}
      <section className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-4">
              Real Backtests
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Strategies That
              <br />
              <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                Perform
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">
              Every strategy is backtested against 10 years of real historical data. See the results yourself.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {strategies.map((s) => (
              <div
                key={s.name}
                className="group relative rounded-2xl overflow-hidden border-2 border-surface-600/30 hover:border-primary-500/50 bg-surface-950 transition-all duration-300 hover:shadow-xl cursor-pointer shadow-md"
                onClick={() => navigate(`/backtest/${s.backtestId}`)}
              >
                <div className="relative">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-48 sm:h-56 object-cover object-top"
                  />
                </div>
                <div className="p-5 border-t border-surface-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-surface-100 group-hover:text-primary-400 transition-colors">
                        {s.name}
                      </h3>
                      <p className="text-xs text-surface-500 mt-0.5">3-year backtest · Click to view →</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-surface-500 uppercase tracking-wider">CAGR</p>
                      <p className="text-2xl font-bold"><span style={{ color: "#22c55e", display: "inline" }}>{s.cagr}</span></p>
                    </div>
                  </div>
                  <div className="flex gap-4 pt-2 border-t border-surface-800/50">
                    <div>
                      <p className="text-xs text-surface-500 uppercase tracking-wider">Sharpe</p>
                      <p className="text-sm font-semibold text-surface-200">{s.sharpe}</p>
                    </div>
                    <div>
                      <p className="text-xs text-surface-500 uppercase tracking-wider">Max DD</p>
                      <p className="text-sm font-semibold text-red-400">{s.maxDrawdown}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Opportunity */}
      <section id="value" className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-950 to-surface-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              The Opportunity
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              A New Revenue Stream,{" "}
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                A Competitive Edge
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-3xl mx-auto">
              Your competitors offer checking, savings, stocks, and mutual funds.{" "}
              <span style={{ color: "#4ade80", display: "inline", fontWeight: 700 }}>You can now offer algo-driven investment strategies — a product your peers don't have.</span>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: (
                  <svg className="w-7 h-7 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ),
                title: "New Product Line",
                desc: "Offer algorithmic trading strategies alongside your existing checking, savings, and investment products. Differentiate from every competitor in your market.",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-accent-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                ),
                title: "Seamless Integration",
                desc: "TradeCraft plugs into your existing infrastructure via API. Customers fund strategies from accounts they already have with you. No new onboarding friction.",
              },
              {
                icon: (
                  <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "Any Exchange, Globally",
                desc: "Not limited to NYSE. TradeCraft is built to integrate with LSE, PSX, TSE, ASX, and any stock exchange worldwide. Your market, your rules.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="group relative p-6 sm:p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50 hover:border-primary-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/10 overflow-hidden"
              >
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-surface-100 mb-3">{item.title}</h3>
                  <p className="text-surface-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-4">
              Use Cases
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Built For{" "}
              <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                Your Industry
              </span>
            </h2>
          </div>

          <div className="grid gap-8">
            {/* Banks */}
            <div className="p-6 sm:p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
                <div className="w-16 h-16 rounded-xl bg-[#052e16] border border-green-500/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">🏦</span>
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-2xl font-bold text-surface-100 mb-3">Banks</h3>
                  <p className="text-surface-400 leading-relaxed mb-4">
                    Your customers already have checking, savings, and investment accounts with you.
                    Now offer them algo-driven strategies as a new product — funded directly from their
                    existing accounts. TradeCraft integrates with your internal banking APIs or connects
                    to an external brokerage partner of your choice. Trades execute through your infrastructure,
                    keeping everything within your ecosystem.
                  </p>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                    {["Integrates with existing accounts", "Your brokerage or partner brokerage", "White-label ready", "Regulatory-compliant architecture"].map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Fund Managers */}
            <div className="p-6 sm:p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
                <div className="w-16 h-16 rounded-xl bg-[#1e1b4b] border border-indigo-500/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">📊</span>
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-2xl font-bold text-surface-100 mb-3">Fund Managers</h3>
                  <p className="text-surface-400 leading-relaxed mb-4">
                    Offer your clients access to backtested, rule-based strategies that execute automatically.
                    Diversify your fund's offerings beyond traditional active management. TradeCraft strategies
                    are transparent — clients see the logic, the backtest, and the performance. You manage the
                    relationship, we provide the engine.
                  </p>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                    {["Transparent strategy logic", "Backtested with real market data", "Daily automated execution", "Multi-strategy allocation"].map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Hedge Funds */}
            <div className="p-6 sm:p-8 rounded-2xl bg-surface-900/80 border border-surface-700/50">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
                <div className="w-16 h-16 rounded-xl bg-[#0f172a] border border-cyan-500/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">⚡</span>
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-2xl font-bold text-surface-100 mb-3">Hedge Funds</h3>
                  <p className="text-surface-400 leading-relaxed mb-4">
                    Deploy quantitative strategies at scale with a battle-tested execution engine.
                    Build proprietary strategies using TradeCraft's DSL or integrate your own models.
                    The platform handles execution, rebalancing, and risk management — freeing your
                    quant team to focus on alpha generation, not infrastructure.
                  </p>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                    {["Proprietary strategy DSL", "Scalable execution engine", "Risk management built-in", "API-first architecture"].map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Integrates — Bank Flow */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-900 to-surface-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-4">
              Integration
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              How It Fits Into{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-primary-400 bg-clip-text text-transparent">
                Your Platform
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">
              A bank customer's journey with TradeCraft strategies — fully integrated.
            </p>
          </div>

          {/* Bank Integration Flow */}
          <div className="relative">
            {/* Step 1 — Customer */}
            <div className="flex justify-center mb-4">
              <div className="px-6 py-4 rounded-xl bg-[#1e293b] border-2 border-indigo-400 text-center shadow-lg shadow-indigo-500/20">
                <span className="text-3xl">👤</span>
                <p className="text-base font-bold text-white mt-2">Bank Customer</p>
                <p className="text-xs text-surface-300">Existing account holder</p>
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-0.5 h-8 bg-indigo-500" />
                <span className="text-xs text-indigo-400 font-medium">logs in</span>
                <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Step 2 — Bank Platform */}
            <div className="mx-auto max-w-lg mb-4">
              <div className="text-center px-6 py-4 rounded-xl bg-[#4338ca] border-2 border-indigo-200 shadow-lg shadow-indigo-400/30">
                <span className="text-2xl">🏦</span>
                <h3 className="text-lg font-bold text-white mt-1">Your Bank Platform</h3>
                <p className="text-sm sm:text-lg font-bold text-white drop-shadow-md">Checking · Savings · Stocks · Mutual Funds · <strong className="text-yellow-200 sm:text-xl">Algo Strategies</strong></p>
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-0.5 h-8 bg-primary-500" />
                <span className="text-xs text-primary-400 font-medium">funds a strategy</span>
                <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Step 3 — TradeCraft Engine */}
            <div className="mx-auto max-w-md mb-4">
              <div className="text-center px-6 py-4 rounded-xl bg-[#4c1d95] border-2 border-purple-300 shadow-lg shadow-purple-500/20">
                <span className="text-2xl">⚡</span>
                <h3 className="text-lg font-bold text-white mt-1">TradeCraft Engine</h3>
                <p className="text-sm text-purple-100">Strategy execution · Rebalancing · Risk mgmt</p>
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-1 h-8 bg-green-500" />
                <span className="text-xs text-green-400 font-medium">executes trades via</span>
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Step 4 — Brokerage Options */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 mb-4">
              <div className="px-5 py-4 rounded-xl bg-[#166534] border-2 border-green-300 text-center w-full sm:w-auto sm:flex-1 max-w-[280px] shadow-lg shadow-green-500/10">
                <span className="text-2xl">🔗</span>
                <p className="text-sm font-bold text-white mt-2">Bank's Internal API</p>
                <p className="text-xs text-green-100 mt-1">Your own brokerage arm</p>
              </div>
              <div className="flex items-center text-surface-300 font-bold text-lg">OR</div>
              <div className="px-5 py-4 rounded-xl bg-[#166534] border-2 border-green-300 text-center w-full sm:w-auto sm:flex-1 max-w-[280px] shadow-lg shadow-green-500/10">
                <span className="text-2xl">🤝</span>
                <p className="text-sm font-bold text-white mt-2">Partner Brokerage</p>
                <p className="text-xs text-green-100 mt-1">External brokerage of choice</p>
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <div className="w-0.5 h-8 bg-green-400" />
            </div>

            {/* Step 5 — Customer Account */}
            <div className="mx-auto max-w-xl mb-4">
              <div className="text-center px-4 sm:px-6 py-5 rounded-2xl bg-[#166534] border-2 border-green-300 shadow-lg shadow-green-500/20">
                <span className="text-3xl">🛡️</span>
                <p className="text-sm sm:text-base font-bold text-white mt-2">Customer's Money Stays in Their Account</p>
                <p className="text-xs sm:text-sm text-green-100 mt-1">
                  Funds never leave your platform. Trades execute in the customer's own account —
                  <span className="hidden sm:inline"><br /></span> full transparency, full compliance, full control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Diagram */}
      <section id="architecture" className="relative py-24 sm:py-32 bg-surface-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
              Architecture
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              Enterprise{" "}
              <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                Integration
              </span>
            </h2>
            <p className="text-lg text-surface-400 max-w-2xl mx-auto">
              How TradeCraft connects institutions to global exchanges.
            </p>
          </div>

          <div className="relative">
            {/* Row 1 — Institutions */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mb-4">
              {[
                { emoji: "🏦", name: "Bank", color: "bg-[#166534] border-green-300" },
                { emoji: "📊", name: "Fund Manager", color: "bg-[#3730a3] border-indigo-300" },
                { emoji: "⚡", name: "Hedge Fund", color: "bg-[#78350f] border-amber-300" },
              ].map((inst) => (
                <div
                  key={inst.name}
                  className={`flex flex-col items-center gap-1 px-4 sm:px-5 py-3 sm:py-4 rounded-xl border-2 min-w-[100px] sm:min-w-[120px] ${inst.color}`}
                >
                  <span className="text-3xl">{inst.emoji}</span>
                  <span className="text-sm font-bold text-white">{inst.name}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-0.5 h-8 bg-indigo-500" />
                <span className="text-xs text-indigo-400 font-medium">API integration</span>
                <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Row 2 — TradeCraft Platform */}
            <div className="mx-auto max-w-lg mb-4">
              <div className="text-center px-6 py-5 rounded-xl bg-[#4c1d95] border-2 border-purple-300 shadow-lg shadow-purple-500/20">
                <span className="text-2xl">⚡</span>
                <h3 className="text-lg font-bold text-white mt-1">TradeCraft Platform</h3>
                <p className="text-sm text-purple-100">Strategy Engine · Risk Management · Execution</p>
                <p className="text-xs text-purple-200 mt-1">White-label · API-first · Multi-tenant</p>
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <div className="flex flex-col items-center">
                <div className="w-1 h-8 bg-green-500" />
                <span className="text-xs text-green-400 font-medium">executes on</span>
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Row 3 — Exchanges */}
            <div id="exchanges" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {[
                { name: "NYSE", location: "New York", flag: "🇺🇸" },
                { name: "NASDAQ", location: "New York", flag: "🇺🇸" },
                { name: "LSE", location: "London", flag: "🇬🇧" },
                { name: "Euronext", location: "Amsterdam", flag: "🇪🇺" },
                { name: "TSX", location: "Toronto", flag: "🇨🇦" },
                { name: "HKEX", location: "Hong Kong", flag: "🇭🇰" },
                { name: "SSE", location: "Shanghai", flag: "🇨🇳" },
                { name: "BSE", location: "Mumbai", flag: "🇮🇳" },
                { name: "TSE", location: "Tokyo", flag: "🇯🇵" },
                { name: "SGX", location: "Singapore", flag: "🇸🇬" },
                { name: "ASX", location: "Sydney", flag: "🇦🇺" },
                { name: "PSX", location: "Karachi", flag: "🇵🇰" },
              ].map((ex) => (
                <div
                  key={ex.name}
                  className="px-4 py-4 rounded-xl bg-[#1e293b] border-2 border-surface-500 text-center hover:border-primary-400 transition-all duration-300 shadow-md"
                >
                  <span className="text-2xl">{ex.flag}</span>
                  <p className="text-base font-bold text-surface-100 mt-2">{ex.name}</p>
                  <p className="text-xs text-surface-400">{ex.location}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-6">
              <p className="text-sm text-surface-500">
                + any exchange with an accessible API — <span style={{ color: "#22c55e", display: "inline" }}>no platform changes required</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiators */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-b from-surface-900 to-surface-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm font-medium mb-4">
              Why TradeCraft
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-4">
              What Sets Us{" "}
              <span className="bg-gradient-to-r from-accent-400 to-primary-400 bg-clip-text text-transparent">
                Apart
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: "🔌", title: "API-First Architecture", desc: "RESTful APIs for every operation. Integrate into your existing platform with minimal engineering effort." },
              { icon: "🏷️", title: "White-Label Ready", desc: "Your brand, your UX, your customer relationship. TradeCraft runs invisibly behind the scenes." },
              { icon: "🌐", title: "Multi-Exchange Support", desc: "Built to connect to any stock exchange globally. NYSE, LSE, PSX, TSE — one platform, any market." },
              { icon: "🔒", title: "Compliance-Friendly", desc: "Customer funds remain in their own accounts. Clean audit trails, full transparency, regulatory alignment." },
              { icon: "📈", title: "Backtested Strategies", desc: "Every strategy is tested against years of real market data. Clients see performance before investing a dollar." },
              { icon: "🏗️", title: "Built by Wall Street Engineers", desc: "Designed and built by engineers with deep experience in institutional trading systems and financial infrastructure." },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-5 rounded-xl bg-surface-900/50 border border-surface-700/50">
                <span className="text-2xl flex-shrink-0">{item.icon}</span>
                <div>
                  <h3 className="text-base font-bold text-surface-100 mb-1">{item.title}</h3>
                  <p className="text-sm text-surface-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-surface-950 to-accent-500/10" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[size:40px_40px]" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-surface-100 mb-6">
            Ready to{" "}
            <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
              Partner?
            </span>
          </h2>
          <p className="text-lg text-surface-400 mb-4 max-w-2xl mx-auto">
            Let's discuss how TradeCraft can become a new product line for your institution.
          </p>
          <p className="text-base text-surface-500 mb-10">
            Schedule a demo with our enterprise team.
          </p>
          <a
            href="mailto:info@colvix.net"
            className="group inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white hover:shadow-2xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-1"
          >
            info@colvix.net
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>

      {/* Footer */}
      <section style={{ background: "#0f172a" }} className="border-t border-surface-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="border-t border-surface-800/50 pt-8 mb-8">
            <div className="space-y-3 text-xs leading-relaxed" style={{ color: "#94a3b8" }}>
              <p>
                <strong style={{ color: "#f1f5f9" }}>Confidential:</strong> This page is intended for institutional partners and prospective enterprise clients.
                TradeCraft is a product of Colvix LLC. All information presented here is for informational purposes only and does not constitute an offer or solicitation.
              </p>
              <p>
                <strong style={{ color: "#f1f5f9" }}>Past performance is not indicative of future results.</strong> All investing involves risk, including the possible loss of principal.
                Strategy backtests are based on historical data and do not guarantee future performance.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ color: "#94a3b8" }}>
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="TradeCraft" className="w-8 h-8" />
              <div>
                <span>© {new Date().getFullYear()} TradeCraft by Colvix LLC. All rights reserved.</span>
                <p className="mt-0.5" style={{ color: "#64748b" }}>Built by ex-Wall Street engineers and traders.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
              <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy</button>
              <button onClick={() => navigate("/faq")} className="hover:text-white transition-colors">FAQ</button>
              <a href="mailto:info@colvix.net" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
